<template>
    <div v-if="isOpen" class="modal fade show custom-modal-backdrop" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h1 class="mb-0">Customize Fields</h1>
                            <button
                                @click="toggleModal"
                                type="button"
                                class="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>
                <hr class="mt-0">
                
                <div class="pb-2 px-2 custom-modal-scroll">
                    <div v-for="(field, index) in customizableFields" :key="index" class="form-check mb-1">
                        <input class="form-check-input" type="checkbox" v-model="field.checked" :id="'custom_chk_' + index" @change="toggleFieldVisibility(field)">
                        <label class="form-check-label custom-cursor-pointer" :for="'custom_chk_' + index">
                            {{ field.label }}
                        </label>
                    </div>
                    <div v-if="customizableFields.length === 0" class="text-center text-muted py-2">
                        No customizable fields found.
                    </div>
                </div>
                
                <div class="col-12 text-center">
                    <hr class="mt-2">
                    <button type="button" class="btn btn-primary me-1 mt-1 mb-1 waves-effect waves-float waves-light" @click="toggleModal">
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import useFormVisibilityEngine from '@/services/utils/formVisibilityEngine'
import handleFormFieldCustomization from '@/services/modules/formFieldCustomization'

const showSuccess = inject('showSuccess');
const showError = inject('showError');
const store = useStore();
const route = useRoute();

const isOpen = ref(false);
const customizableFields = ref([]);
const slotRef = ref(null);

const fieldsConfig = computed(() => store.getters['formFieldCustomization/getFieldsConfig']);

const setFieldsConfig = (config) => {
    store.commit('formFieldCustomization/setFieldsConfig', config);
};

const toggleModal = () => {
    isOpen.value = !isOpen.value;
};

const openModal = (refSlot) => {
    slotRef.value = refSlot;
    customizableFields.value = [];
    if (!refSlot) return;
    
    const { generateStableKey, findFieldWrapper, getLabelForInput, CUSTOM_ELEMENT_SELECTOR } = useFormVisibilityEngine();
    const uniqueFieldsMap = new Map();
    const inputs = refSlot.querySelectorAll('input:not([type="hidden"]):not([type="button"]):not([type="submit"]), select, textarea');
    const currentConfig = fieldsConfig.value || {};
    
    inputs.forEach((input) => {
        if (input.closest('.modal') || input.readOnly || input.hasAttribute('readonly')) return;

        let labelText = getLabelForInput(input, refSlot, findFieldWrapper);

        const key = generateStableKey(
            input.getAttribute('name'), 
            input.getAttribute('id'), 
            input.getAttribute('placeholder'),
            labelText
        );
        
        if (!key) return;
        
        if (labelText) labelText = labelText.replace(/[*:]/g, '').trim();
        if (!labelText && input.placeholder) labelText = input.placeholder.trim();
        if (!labelText && input.name) labelText = input.name.trim();
        if (!labelText) labelText = key;

        if (uniqueFieldsMap.has(key)) {
            uniqueFieldsMap.get(key).elements.push(input);
            return;
        }

        let isChecked = currentConfig[key] !== false;
        uniqueFieldsMap.set(key, {
            key: key,
            label: labelText,
            checked: isChecked,
            elements: [input]
        });
    });

    refSlot.querySelectorAll(CUSTOM_ELEMENT_SELECTOR).forEach((element) => {
        if (element.closest('.modal')) return;

        const key = element.getAttribute('data-customize-key');
        if (!key) return;

        if (uniqueFieldsMap.has(key)) {
            uniqueFieldsMap.get(key).elements.push(element);
            return;
        }

        uniqueFieldsMap.set(key, {
            key: key,
            label: element.getAttribute('data-customize-label') || key,
            checked: currentConfig[key] !== false,
            elements: [element]
        });
    });

    customizableFields.value = Array.from(uniqueFieldsMap.values());
    isOpen.value = true;
};

const saveConfiguration = async (closeModal = true) => {
    const { saveConfig } = handleFormFieldCustomization();
    const companyId = route.params.companyId;
    const moduleId = route.params.moduleId;
    const routeName = route.name;
    
    const finalConfig = {};
    customizableFields.value.forEach(f => {
        finalConfig[f.key] = f.checked;
    });
    
    try {
        await saveConfig(companyId, moduleId, routeName, finalConfig);
        
        const cacheKey = `form_customizations_${companyId}_${moduleId}`;
        
        let allConfigs = [];
        try {
            const cachedData = localStorage.getItem(cacheKey);
            if (cachedData) allConfigs = JSON.parse(cachedData);
        } catch(e) {
            console.log("Error in parsing customization data", e);
        }
        
        const existingIndex = allConfigs.findIndex(c => c.route_name === routeName);
        if (existingIndex > -1) {
            allConfigs[existingIndex].fields_config = finalConfig;
        }
        if (existingIndex <= -1) {
            allConfigs.push({
                route_name: routeName,
                fields_config: finalConfig,
                company_id: companyId,
                module_id: moduleId
            });
        }
        localStorage.setItem(cacheKey, JSON.stringify(allConfigs));

        if (showSuccess) showSuccess("Customization saved successfully!");
    } catch (err) {
        showError("Failed to save configuration.");
    }
    
    if (!closeModal) return;
    toggleModal();
};

const toggleFieldVisibility = async (field) => {
    const newConfig = { ...fieldsConfig.value };
    newConfig[field.key] = field.checked;
    setFieldsConfig(newConfig);
    
    const { applyVisibilityRules } = useFormVisibilityEngine();
    applyVisibilityRules(slotRef.value);

    await saveConfiguration(false);
};

defineExpose({
    openModal
});
</script>

<style scoped>
.custom-modal-backdrop {
    display: block;
    background: #1c1d1c33;
}

.custom-modal-scroll {
    max-height: 60vh;
    overflow-y: auto;
}

.custom-cursor-pointer {
    cursor: pointer;
}
</style>
